import React, { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";

import { ValueType } from "atoms/types";
import ResponsiveDiv from "atoms/ResponsiveDiv";
import Image from "atoms/Image";
import Text from "atoms/Text";
import Flexbox from "atoms/Flexbox";
import { FlexboxItem } from "atoms/Flexbox/Flexbox";
import { SchemaModel, StringType, ArrayType } from "schema-typed";
import { BookDemoFormProps, CreateFormType, FormValidationError } from "./types";
import { useNavigate } from "react-router-dom";

// services and events
import { createBookDemoForm } from "services/bookDemoForm";
import { fireEmailEvent } from "events/emailEvent";
import { fireLeadGeneratedEvent } from "events/leadGenerated";
import { fireOrderEvent } from "events/orderEvent";
import { firePlatformEvent } from "events/platformEvent";
import { fireCategoryEvent } from "events/categoryEvent";
import { fireGstEvent } from "events/gstEvent";
import { fireBrandNameEvent } from "events/brandNameEvent";
import { fireSellerNameEvent } from "events/sellerNameEvents";
import { firePhoneNumberEvent } from "events/phoneNumberEvent";
import { fireErrorEvent } from "events/errorEvent";
import Input from "molecules/Input";
import Radio from "molecules/Radio";
import RadioGroup from "molecules/RadioGroup";
import Header from "organisms/Header";
import Footer from "organisms/Footer";
import Button from "atoms/Button";
import ArrowWithTail from "icons/ArrowWithTail";
import Spinner from "atoms/Spinner";
import DropDown from "molecules/DropDown";
import Modal from "molecules/Modal";
import Animated from "molecules/Animated";
import Checkbox from "molecules/Checkbox";
import { getStartedCtaClicked } from "events/getStartedEvent";
import HashLink from "atoms/HashLink";
import { createContactNumberLead } from "services/contactNumberLead";
import WidgetContainer from "atoms/WidgetContainer";
import PageContainer from "atoms/PageContainer";
import { fireProductLinkEvent } from "events/productLinkEvents";
import { validateProductUrl } from "constants/bookDemoFormDetails";

const categoryList = [
	"Apparel",
	"Home Decor/ Furnishing/Kitchen",
	"Fashion accessories",
	"Health & Fitness",
	"FMCG/ Consumer Goods",
	"Beauty & Cosmetics",
	"Others (e.g., Jewellery, Footwear, Toys etc.)"
];
const categoryData = categoryList.map(datum => {
	return {
		label: datum,
		value: datum
	};
});

const platformList = [
	"Amazon/ Flipkart/ Meesho/ Other Marketplace",
	"Own Website",
	"Both (Marketplace and Own Website)",
	"None"
];
const platformData = platformList.map(datum => {
	return {
		label: datum,
		value: datum
	};
});

const numbersOfOrderData = ["0", "1-20", "20-100", "100+"].map(datum => {
	return {
		label: datum,
		value: datum
	};
});

const validationModel = SchemaModel({
	sellerName: StringType()
		.isRequired("This field is mandatory.")
		.pattern(/^[a-zA-Z\s]*$/, "Seller Name should contain only letters"),
	contactNumber: StringType()
		.isRequired("This field is mandatory")
		.pattern(/^[6-9][0-9]{9}$/, "Phone Enter Valid Contact Number")
		.rangeLength(10, 10, "Please enter a 10-digit Contact number"),
	hasGst: StringType().isRequired("This field is mandatory"),
	numberOfOrders: StringType().isRequired("This field is mandatory"),
	platform: ArrayType().minLength(1, "This field is mandatory"),
	categories: ArrayType().minLength(1, "This field is mandatory"),
	email: StringType()
		.isRequired()
		.isEmail("Invalid email address"),
	productLink: StringType()
		.isRequired("This field is mandatory")
		.addRule((value) => {
			return validateProductUrl(value);
		}, "Please enter a valid URL")
});

const initialDetailsValidation: FormValidationError = {
	sellerName: {
		hasError: false
	},
	contactNumber: {
		hasError: false
	},
	brandName: { hasError: false },
	categories: { hasError: false },
	hasGst: { hasError: false },
	platform: { hasError: false },
	numberOfOrders: { hasError: false },
	email: { hasError: false },
	productLink: { hasError: false }
};

const styles = {
	formWrapper: css`
		background-color: var(--color-dark-gray);
		border-radius: var(--spacing-xxl);
		padding: var(--spacing-xxl);
		position: relative;
	`,
	loaderWrapper: css`
		position: absolute;
		left: 0;
		top: 0;
		right: 0;
		bottom: 0;
		z-index: 2000;
		background-color: rgba(0, 0, 0, 0.4);
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
	`,
	videoIframe: css`
		width: 100%;
		max-width: 650px;
		overflow: hidden;
		border-radius: var(--spacing-md);
		margin: 0 var(--spacing-md);
	`
};

const SuccessGif = () => {
	return (
		<Flexbox direction="row" justify="center" align="center" mb="sm">
			<Image
				height={128}
				width={128}
				src="https://d2z53scj8veve3.cloudfront.net/shopdeck-home/form-completed.gif"
			/>
		</Flexbox>
	);
};

export default (props: BookDemoFormProps) => {
	const { showBackButton = true } = props;
	const navigate = useNavigate();
	const [validationErrors, setValidationErrors] = React.useState<
		FormValidationError
	>(initialDetailsValidation);

	const [formData, setFormData] = useState<CreateFormType>({
		sellerName: "",
		brandName: "",
		contactNumber: "",
		hasGst: "",
		platform: [],
		numberOfOrders: "",
		categories: [],
		email: "",
		productLink: ""
	});

	const [loading, setLoading] = useState<boolean>(false);
	const [formScreen, setFormScreen] = React.useState<"form" | "thankyou">(
		"form"
	);
	const [showModal, setShowModal] = React.useState(false);
	const [isCheckedInfo, setIsCheckedInfo] = React.useState(true);
    const [showThirdPageForm, setShowThirdPageForm] = React.useState(false);

	const toggleModalVisibility = useCallback(() => {
		setShowModal(!showModal);
		getStartedCtaClicked({ sellerName: formData.sellerName, contactNumber: formData.contactNumber });
	}, [showModal, formData]);

	// check all the fields except product link
	const isFormValid = Object.entries(validationModel.check(formData))
	.filter(([key]) => key !== "productLink")
	.every(([, field]) => !field.hasError);

	const productLinkValidation = !validationErrors.productLink?.hasError && formData.productLink !== "";

	const isPhaseOneFormValid =
		Object.values({
			contactNumber: validationErrors.contactNumber,
			sellerName: validationErrors.sellerName
		}).every(field => !field?.hasError) &&
		formData.contactNumber !== "" &&
		formData.sellerName !== "";

	useEffect(() => {
		if (!Object.values(validationErrors).every(field => !field.hasError)) {
			Object.values(validationErrors)
				.filter(field => field.hasError)
				.map(field => {
					return fireErrorEvent(field.errorMessage);
				});
		}
	}, [validationErrors]);

	const handleContactNumberChange = useCallback(
		async (event) => {
			const value = event.target.value;
			const updatedFormData = { ...formData, contactNumber: value };
			const validation = validationModel.checkForField("contactNumber", updatedFormData)
			setFormData(updatedFormData);
			setValidationErrors({ ...validationErrors, contactNumber: validation });
			if (!validation.hasError) {
				firePhoneNumberEvent(value);
				await createContactNumberLead({
					contact_number: value,
					seller_name: formData.sellerName,
				});
			}
		},
		[formData, validationModel, validationErrors, firePhoneNumberEvent]
	)

	const handleSellerNameChange = useCallback(
		event => {
			const value = event.target.value;
			const updatedFormData = { ...formData, sellerName: value };
			const validation = validationModel.checkForField(
				"sellerName",
				updatedFormData
			);
			setFormData(updatedFormData);
			setValidationErrors({ ...validationErrors, sellerName: validation });
			if (value.trim() !== "") {
				fireSellerNameEvent(value);
			}
		},
		[formData, validationModel, validationErrors, fireBrandNameEvent]
	);

	const handleGstNumberChange = useCallback(
		(value: ValueType): void => {
			const updatedFormData = { ...formData, hasGst: value + "" };
			const validation = validationModel.checkForField(
				"hasGst",
				updatedFormData
			);
			setValidationErrors({ ...validationErrors, hasGst: validation });
			setFormData(updatedFormData);
			fireGstEvent(value + "");
		},
		[formData, fireGstEvent, validationModel]
	);

	const handleCategoryChange = useCallback(
		(values): void => {
			let updatedFormData = { ...formData, categories: values };
			setFormData(updatedFormData);
			let finalCategories = [...values];
			fireCategoryEvent(finalCategories);
		},
		[formData, validationModel, validationErrors, fireCategoryEvent]
	);

	const handlePlatformChange = useCallback(
		(value: ValueType[]): void => {
			const parsedValue = value.map(v => v.toString());
			const updatedFormData = { ...formData, platform: parsedValue };
			setFormData(updatedFormData);
			firePlatformEvent(parsedValue);
		},
		[formData, firePlatformEvent]
	);

	const handleNumberOfOrdersChange = useCallback(
		(value: ValueType[]): void => {
			const updatedFormData = {
				...formData,
				numberOfOrders: value.join("") + ""
			};
			setFormData(updatedFormData);
			fireOrderEvent(value + "");
		},
		[formData, fireOrderEvent]
	);

	const handleEmailChange = useCallback(
		event => {
			const value = event.target.value;
			const updatedFormData = { ...formData, email: value };
			const validation = validationModel.checkForField(
				"email",
				updatedFormData
			);
			setFormData(updatedFormData);
			setValidationErrors({ ...validationErrors, email: validation });
			if (!validation.hasError) {
				fireEmailEvent(value);
			}
		},
		[formData, validationModel, validationErrors, fireEmailEvent]
	);

	const isNoneDisabled = formData.platform.includes("None")
		? false
		: formData.platform.length > 0;

	const handleBookDemoForm = useCallback((): void => {
		const {
			contactNumber,
			sellerName,
			hasGst,
			platform,
			numberOfOrders,
			categories,
			email,
			productLink
		} = formData;

		// Prepare API request data with defaults only when submitting
		const requestData = {
			seller_name: sellerName,
			brand_name: "",
			contact_number: contactNumber,
			// Apply defaults only for API request
			has_gst: hasGst || "",
			shopping_platform: platform.length > 0 ? platform : [],
			number_of_orders: numberOfOrders || "",
			categories: categories,
			email: email || "",
			product_link: productLink || ""
		};

		setLoading(true);
		createBookDemoForm(requestData) // If User Skip p-2  send one P1 Payload OTher Wise Whole Request
			.then((res) => {
				setLoading(false);
				setFormScreen("thankyou");
				fireLeadGeneratedEvent({
					...formData,
					hasGst: formData.hasGst || "",
					platform: formData.platform.length > 0 ? formData.platform : [""],
					numberOfOrders: formData.numberOfOrders || "",
					categories:
						formData.categories.length > 0 ? formData.categories : [""],
					email: formData.email || "",
					fbEventLabel: res?.payload ? res?.payload?.fb_label_name : "",
					productLink: formData.productLink || ""
				});
				// When Done Submitting Just Scroll to Top
				//window && window.scrollTo(0, 0);
			})
			.catch(() => {
				setLoading(false);
				// When Done Submitting Just Scroll to Top
				window && window.scrollTo(0, 0);
			});
	}, [
		formData,
		setLoading,
		createBookDemoForm,
		navigate,
		fireLeadGeneratedEvent
	]);

	const handleNextClicked = () => {
		setShowModal(false);
		setShowThirdPageForm(true);
	};
    
	const handleProductLinkChange = useCallback(
		event => {
			const value = event.target.value;
			const updatedFormData = { ...formData, productLink: value };
			const validation = validationModel.checkForField(
				"productLink",
				updatedFormData
			);
			setFormData(updatedFormData);
			setValidationErrors({ ...validationErrors, productLink: validation });
			if (!validation.hasError) {
				fireProductLinkEvent(value);
			}
		},
		[formData, validationModel, validationErrors, fireProductLinkEvent]
	);

	return (
		<PageContainer id="demo-form">
			<Animated
				animation="fadeInUp"
				duration={0.5}
				iteration={1}
				timingFunction="ease"
				trigger={"inview"}
			>
				{formScreen === "form" && (
					<Flexbox direction="row" justify="center" align="center">
						<FlexboxItem colspan={24} colspanXL={18}>
							<Text
								mb="sm"
								as="h1"
								color="white"
								fontStyleGuide="manrope-heading4"
								fontStyleGuideXL="manrope-heading2"
								align="center"
							>
								Book a &nbsp;
								<Text
									as="span"
									primaryGradient
									fontStyleGuide="manrope-heading4"
									fontStyleGuideXL="manrope-heading2"
									align="center"
								>
									Free Demo
								</Text>
							</Text>
							<Text
								as="p"
								color="white"
								align="center"
								fontStyleGuide="body3"
								mb="xxl"
							>
								To understand how Shopdeck can help you setup and grow your
								D2C online business, profitably!
							</Text>
							<ResponsiveDiv css={styles.formWrapper}>
								{loading && (
									<ResponsiveDiv css={styles.loaderWrapper}>
										<Spinner />
									</ResponsiveDiv>
								)}
								<Input
									fullWidth={true}
									mb="sm"
									title="Your Name"
									required={true}
									placeholder="Enter your name"
									name="sellerName"
									maxLength={40}
									value={formData.sellerName}
									onChange={handleSellerNameChange}
									validationType={
										validationErrors.sellerName?.hasError
											? "error"
											: undefined
									}
									validationMessage={
										validationErrors.sellerName?.hasError
											? validationErrors.sellerName.errorMessage
											: undefined
									}
								/>
								<Flexbox
									direction="row"
									justify="space-between"
									align="center"
									gap={16}
									mb="sm"
								>
									<FlexboxItem colspan={24} colspanXL={24}>
										<Input
											fullWidth={true}
											leftAddon={"+91"}
											title="Phone Number"
											required={true}
											maxLength={10}
											placeholder="Enter valid 10-digit phone number"
											name="contactNumber"
											value={formData.contactNumber}
											onChange={handleContactNumberChange}
											validationType={
												validationErrors.contactNumber?.hasError
													? "error"
													: undefined
											}
											validationMessage={
												validationErrors.contactNumber?.hasError
													? validationErrors.contactNumber.errorMessage
													: undefined
											}
											autoComplete="off"
											type="tel"
										/>
									</FlexboxItem>
								</Flexbox>
								<Flexbox direction="row" align="center" justify="flex-start">
									<Checkbox
										checked={isCheckedInfo}
										value="info"
										onChange={() => setIsCheckedInfo(!isCheckedInfo)}
									/>
									<FlexboxItem colspan={20}>
										<Text
											as="div"
											color="white"
											fontStyleGuide="body4"
											ml="sm"
										>
											I have read the{" "}
											<HashLink
												to={"/terms"}
												css={css`
														color: var(--color-primary);
													`}
											>
												T&C{" "}
											</HashLink>
											and{" "}
											<HashLink
												to={"/privacy-policy"}
												css={css`
														color: var(--color-primary);
													`}
											>
												Privacy Policy.
											</HashLink>
										</Text>
									</FlexboxItem>
								</Flexbox>
								<ResponsiveDiv mt="2xl">
									<Button
										type="primary"
										full
										onClick={toggleModalVisibility}
										disabled={!isPhaseOneFormValid || loading || !isCheckedInfo}
									>
										Get Started
									</Button>
								</ResponsiveDiv>
								<Modal
									isCloseEnabled={false}
									isOpen={showModal}
									onClose={toggleModalVisibility}
									size="sm"
								>
									<Modal.Header>
										<Text color="white" fontStyleGuide="manrope-heading5">
											Additional Info to assist you better
										</Text>
									</Modal.Header>
									<Modal.Body>
										<ResponsiveDiv mb="xxl">
											<Text mb="sm" color="white" fontStyleGuide="body5">
												Do you have a GST? <sup>&nbsp;*</sup>
											</Text>
											<RadioGroup
												value={formData.hasGst}
												onChange={handleGstNumberChange}
											>
												<Flexbox
													direction="row"
													justify="flex-start"
													align="center"
												>
													<Radio value="Yes" mr="lg">
														Yes
													</Radio>
													<Radio value="Already Applied" mr="lg">
														Already Applied
													</Radio>
													<Radio value="Not Applied Yet">Not Applied Yet</Radio>
												</Flexbox>
											</RadioGroup>
										</ResponsiveDiv>
										<ResponsiveDiv mb="xxl">
											<Text mb="sm" color="white" fontStyleGuide="body5">
												What are the category of products you sell?
												<sup>&nbsp;*</sup>
											</Text>
											<DropDown
												data={categoryData}
												preSelectedValues={formData.categories}
												variant="single-select"
												onChange={values => {
													handleCategoryChange(values);
												}}
											/>
										</ResponsiveDiv>
										<ResponsiveDiv mb="xxl">
											<Text mb="sm" color="white" fontStyleGuide="body5">
												Where do you sell your product online?
												<sup>&nbsp;*</sup>
											</Text>
											<DropDown
												data={platformData}
												preSelectedValues={formData.platform}
												variant="single-select"
												onChange={handlePlatformChange}
											/>
										</ResponsiveDiv>
										<ResponsiveDiv mb="xxl">
											<Text mb="sm" color="white" fontStyleGuide="body5">
												How many online orders do you currently do per month?
												<sup>&nbsp;*</sup>
											</Text>
											<DropDown
												data={numbersOfOrderData}
												preSelectedValues={
													formData.numberOfOrders
														? [formData.numberOfOrders]
														: []
												}
												variant="single-select"
												onChange={handleNumberOfOrdersChange}
											/>
										</ResponsiveDiv>
										<FlexboxItem colspan={24} colspanXL={24}>
											<Input
												fullWidth={true}
												required={true}
												title="E-mail Address"
												placeholder="Enter your mail address"
												name="email"
												value={formData.email}
												onChange={handleEmailChange}
												validationType={
													validationErrors.email?.hasError
														? "error"
														: undefined
												}
												validationMessage={
													validationErrors.email?.hasError
														? validationErrors.email.errorMessage
														: undefined
												}
											/>
										</FlexboxItem>
									</Modal.Body>
									<Modal.Footer>
										<Flexbox direction="row" align="center" justify="end">
											<FlexboxItem mb="sm" colspanXL={24} colspan={24}>
												<Button
													full
													onClick={handleNextClicked}
													type="primary"
													disabled={!isFormValid || loading}
												>
													Submit
												</Button>
											</FlexboxItem>
										</Flexbox>
									</Modal.Footer>
								</Modal>
								<Modal 	
								    isCloseEnabled={false}
									isOpen={showThirdPageForm}
									onClose={toggleModalVisibility}
									size="sm">
									<Modal.Header>
										<Text color="white" fontStyleGuide="manrope-heading5">
										   Share a link to your product listing to personalise your demo 
										</Text>
									</Modal.Header>
									<Modal.Body>
										<Input
											fullWidth={true}
											required={true}
											title="Product link"
											placeholder="https://www.amazon.in/your-product-name…"
											name="productLink"
											value={formData.productLink}
											onChange={handleProductLinkChange}
											validationType={
												validationErrors.productLink?.hasError
													? "error"
													: undefined
												}
											validationMessage={
												validationErrors.productLink?.hasError
													? validationErrors.productLink.errorMessage
													: undefined
												}
											/>
											<Flexbox direction="row" justify="flex-start" align="center">
												<Text color="white" fontStyleGuide="body5">
											    	Share your product link and get a chance to win ₹2,000 worth of Facebook & Instagram ads budget — fully paid by Shopdeck!
												</Text>	
										    </Flexbox>	
									</Modal.Body>
									<Modal.Footer>
										<Flexbox direction="row" align="center" justify="end">
											<FlexboxItem mb="sm" colspanXL={24} colspan={24}>
												<Button
													full
													onClick={handleBookDemoForm}
													type="primary"
													disabled={!productLinkValidation || loading}
												>
													Submit Product Link
												</Button>
											</FlexboxItem>
										</Flexbox>
									</Modal.Footer>
								</Modal>
							</ResponsiveDiv>
						</FlexboxItem>
					</Flexbox>
				)}
			</Animated>
			{formScreen === "thankyou" && (
				<Flexbox
					direction="row"
					justify="center"
					align="center"
					mt="2xl"
					mb="2xl"
				>
					<FlexboxItem colspan={24}>
						<SuccessGif />
					</FlexboxItem>
					<FlexboxItem colspan={24} colspanXL={12}>
						<Text
							fontStyleGuide="manrope-heading3"
							primaryGradient
							align="center"
							mb="sm"
						>
							Thank you for your interest!
						</Text>
						<Text
							as="p"
							color="white"
							align="center"
							fontStyleGuide="body3"
							mb="2xl"
							textWrap="balance"
						>
							{formData.hasGst !== "Yes" ? `We partner exclusively with GST-registered sellers. Update your GST status to book a free demo with us.` : "Our expert will connect with you shortly to show you how we can help you scale your brand."}
						</Text>
						<Text
							as="p"
							fontStyleGuide="heading5"
							color="white"
							align="center"
							mb="xxl"
							textWrap="balance"
						>
							Meanwhile get to know more about how{" "}
							<Text as="span" primaryGradient fontStyleGuide="heading5">
								ShopDeck
							</Text>{" "}
							can help you here
						</Text>
						<Flexbox direction="row" justify="center" align="center" mb="2xl">
							<iframe
								width="650" //fit full modal screen
								height="370.3" //fit full modal screen
								src="https://www.youtube.com/embed/L4iNQwpTf1k"
								title="YouTube video player"
								allow="autoplay; encrypted-media; picture-in-picture"
								css={styles.videoIframe}
							/>
						</Flexbox>
						{showBackButton && <Flexbox direction="row" justify="center" align="center">
							<Button type="primary" onClick={() => navigate("/")}>
								<Flexbox direction="row" justify="center" align="center">
									<ArrowWithTail width={16} height={16} direction={"left"} />
									&nbsp; Back to Home
								</Flexbox>
							</Button>
						</Flexbox>}
					</FlexboxItem>
				</Flexbox>
			)}
		</PageContainer>
	);
};
